import type { Row } from '@tanstack/react-table';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import type { EnrichedPortfolioReport } from './usePortfolioTableState.types';

const INITIAL_BATCH = 8;
const BATCH_SIZE = 6;
const BATCH_DELAY_MS = 60;

export function usePortfolioProgressiveRows({
  allRows,
  expandedPortfolioId,
  sortingKey,
  filtersKey,
  page,
  pageSize,
  displayedCount,
  displayedFirstId,
}: {
  allRows: Row<EnrichedPortfolioReport>[];
  expandedPortfolioId: string | null;
  sortingKey: string;
  filtersKey: string;
  page: number;
  pageSize: number;
  displayedCount: number;
  displayedFirstId?: string;
}) {
  const visibleRowCount = allRows.length;
  const dataKey = `${displayedCount}:${displayedFirstId}:${sortingKey}:${filtersKey}:${page}:${pageSize}`;

  const [renderedCount, setRenderedCount] = useState(INITIAL_BATCH);
  const progressiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const prevDataKeyRef = useRef(dataKey);

  let effectiveRenderCount = renderedCount;
  if (prevDataKeyRef.current !== dataKey) {
    effectiveRenderCount = INITIAL_BATCH;
  }

  useEffect(() => {
    if (progressiveTimerRef.current) clearTimeout(progressiveTimerRef.current);
    prevDataKeyRef.current = dataKey;
    setRenderedCount(INITIAL_BATCH);
  }, [dataKey]);

  useEffect(() => {
    if (renderedCount < visibleRowCount) {
      progressiveTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setRenderedCount((prev) =>
            Math.min(prev + BATCH_SIZE, visibleRowCount),
          );
        });
      }, BATCH_DELAY_MS);
      return () => {
        if (progressiveTimerRef.current) {
          clearTimeout(progressiveTimerRef.current);
        }
      };
    }
  }, [renderedCount, visibleRowCount]);

  const rowsToRender = useMemo(() => {
    if (expandedPortfolioId) {
      const idx = allRows.findIndex(
        (r) => r.original.id === expandedPortfolioId,
      );
      if (idx >= 0 && idx >= effectiveRenderCount) {
        return allRows.slice(0, idx + 1);
      }
    }
    return allRows.slice(0, effectiveRenderCount);
  }, [allRows, effectiveRenderCount, expandedPortfolioId]);

  return {
    rowsToRender,
    effectiveRenderCount,
    visibleRowCount,
  };
}
